import { CircleCheckBig } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { typographyVariants } from '@/components/ui/typography';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/utils';
import type { SyncLogView } from '../../services/pmsConnectionApi';
import { integrationStatusCopy as copy } from './copy';

interface Props {
  sync: SyncLogView;
  /** True when this page's save started the running sync (`useSyncProgress().runningOrigin === 'settings'`). */
  startedHere: boolean;
  /**
   * True only when the completed log is the one THIS page started
   * (`useSyncProgress().completedOrigin === 'settings'`) — a sync started on
   * Properties or the landing page must not announce itself here.
   */
  justCompleted: boolean;
}

const ViewPropertiesLink = () => (
  <Link
    to={ROUTES.PROPERTIES}
    className={cn(
      typographyVariants({ variant: 'body-sm-medium' }),
      'ml-auto whitespace-nowrap text-primary underline-offset-4 hover:underline',
    )}
  >
    {copy.syncBanner.viewProperties}
  </Link>
);

/**
 * In-place sync progress for the vendor settings page (spec §3.7). Saving no
 * longer navigates to Properties; this is where the user watches the sync.
 *
 * The page margins live on the wrapper, never on the `Alert` — the primitive is
 * `w-full`, so `mx-5` on it would resolve to 100% of the parent PLUS 40px of
 * margin and hang 40px off the right edge. Same shape as
 * `DisabledIntegrationBanner`, which wraps for this reason.
 */
export const SyncProgressBanner = ({ sync, startedHere, justCompleted }: Props) => {
  if (sync.kind === 'running') {
    return (
      <div className="mx-5 mt-5">
        <Alert variant="info" layout="row" role="status" className="items-center rounded-xl">
          <Spinner size="sm" aria-hidden="true" />
          <AlertDescription className="flex flex-1 flex-wrap items-center gap-x-3">
            {startedHere ? copy.syncBanner.running : copy.syncBanner.runningPassive}
            <ViewPropertiesLink />
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  if (sync.kind === 'complete' && justCompleted) {
    return (
      <div className="mx-5 mt-5">
        <Alert variant="success" layout="row" role="status" className="items-center rounded-xl">
          <CircleCheckBig className="h-[18px] w-[18px]" aria-hidden="true" />
          <AlertDescription className="flex flex-1 flex-wrap items-center gap-x-3">
            {copy.syncBanner.complete}
            <ViewPropertiesLink />
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  return null;
};
